// Admin Mission Wall manager — add, edit, publish/hide, remove partner files.
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Eye, EyeOff, Pencil, Trash2, Plus, X } from "lucide-react";
import { adminPartners, adminCreatePartner, adminUpdatePartner, adminDeletePartner, errText } from "@/lib/api";
import { inputCls, goldBtnCls, ghostBtnCls, fmtDate } from "@/components/admin/constants";

const CONTRIBUTIONS = ["Supplied the Mission", "Funded the Mission", "Hosted the Mission", "Equipped the Mission", "Briefed the Mission", "Taught the Mission", "Opened the Door"];
const WORLDS = ["", "Beauty", "Fashion", "Hospitality", "Entertainment", "Lifestyle", "Technology", "Art", "Food & Beverage", "Community", "Other"];
const EMPTY = { name: "", contribution: CONTRIBUTIONS[0], mission_title: "", world: "", is_public: true };

export const PartnersPanel = () => {
  const [rows, setRows] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => adminPartners().then(setRows).catch((e) => toast.error(errText(e)));
  useEffect(() => { load(); }, []);

  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const save = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error("Partner name is required.");
    setSaving(true);
    try {
      if (editId) {
        await adminUpdatePartner(editId, form);
        toast.success("Partner file updated.");
      } else {
        await adminCreatePartner(form);
        toast.success("Mission activated — partner added to the wall.");
      }
      setForm(EMPTY);
      setEditId(null);
      load();
    } catch (err) {
      toast.error(errText(err));
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (r) => {
    setEditId(r.id);
    setForm({ name: r.name, contribution: r.contribution, mission_title: r.mission_title || "", world: r.world || "", is_public: r.is_public });
  };

  const togglePublic = async (r) => {
    try {
      await adminUpdatePartner(r.id, { is_public: !r.is_public });
      setRows((p) => p.map((x) => (x.id === r.id ? { ...x, is_public: !r.is_public } : x)));
    } catch (err) { toast.error(errText(err)); }
  };

  const remove = async (r) => {
    if (!window.confirm(`Remove ${r.name} from the Mission Wall?`)) return;
    try {
      await adminDeletePartner(r.id);
      setRows((p) => p.filter((x) => x.id !== r.id));
      toast.success("Partner file removed.");
    } catch (err) { toast.error(errText(err)); }
  };

  return (
    <div className="mt-6" data-testid="partners-panel">
      <form onSubmit={save} className="border border-gold/25 bg-charcoal p-5" data-testid="partner-form">
        <p className="font-cinzel text-[10px] uppercase tracking-[0.35em] text-gold">{editId ? "Edit partner file" : "Activate a mission"}</p>
        <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <input value={form.name} onChange={set("name")} placeholder="Partner / brand name *" data-testid="partner-name-input" className={inputCls} />
          <select value={form.contribution} onChange={set("contribution")} data-testid="partner-contribution-select" className={inputCls}>
            {CONTRIBUTIONS.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <input value={form.mission_title} onChange={set("mission_title")} placeholder="Mission / campaign title" data-testid="partner-mission-input" className={inputCls} />
          <select value={form.world} onChange={set("world")} data-testid="partner-world-select" className={inputCls}>
            {WORLDS.map((w) => <option key={w} value={w}>{w || "Creative world (optional)"}</option>)}
          </select>
        </div>
        <div className="mt-4 flex flex-wrap items-center gap-3">
          <label className="flex cursor-pointer items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-silver/80">
            <input type="checkbox" checked={form.is_public} onChange={(e) => setForm((p) => ({ ...p, is_public: e.target.checked }))} data-testid="partner-public-checkbox" className="accent-[#C9A227]" />
            Show on public wall
          </label>
          <button type="submit" disabled={saving} data-testid="partner-save-btn" className={goldBtnCls + " inline-flex items-center gap-2 px-5 py-2.5 text-xs"}>
            <Plus size={13} /> {saving ? "Saving..." : editId ? "Save Changes" : "Add to the Wall"}
          </button>
          {editId && (
            <button type="button" onClick={() => { setEditId(null); setForm(EMPTY); }} data-testid="partner-cancel-edit-btn" className={ghostBtnCls + " inline-flex items-center gap-2 px-4 py-2.5 text-xs"}>
              <X size={13} /> Cancel
            </button>
          )}
        </div>
      </form>

      <div className="mt-5 overflow-x-auto border border-silver/20 bg-charcoal/60">
        {!rows ? (
          <p className="p-6 font-mono text-[10px] uppercase tracking-[0.3em] text-silver/50">Loading partner files...</p>
        ) : rows.length === 0 ? (
          <p className="p-6 font-mono text-[10px] uppercase tracking-[0.3em] text-silver/50" data-testid="partners-empty">No partners yet — the wall is awaiting its first activation.</p>
        ) : (
          <table className="w-full min-w-[760px] text-left" data-testid="partners-table">
            <thead>
              <tr className="border-b border-gold/20 font-mono text-[9px] uppercase tracking-[0.25em] text-silver/50">
                <th className="px-4 py-3">Partner</th><th className="px-4 py-3">Contribution</th><th className="px-4 py-3">Mission</th><th className="px-4 py-3">World</th><th className="px-4 py-3">Activated</th><th className="px-4 py-3">Visibility</th><th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-b border-silver/10 transition-colors hover:bg-onyx/60" data-testid={`partner-row-${r.id}`}>
                  <td className="px-4 py-3 font-head text-sm font-extrabold uppercase tracking-wide text-cream">{r.name}</td>
                  <td className="px-4 py-3 font-body text-sm text-silver/80">{r.contribution}</td>
                  <td className="px-4 py-3 font-body text-sm text-silver/70">{r.mission_title || "—"}</td>
                  <td className="px-4 py-3 font-body text-sm text-silver/70">{r.world || "—"}</td>
                  <td className="px-4 py-3 font-mono text-xs text-silver/60">{fmtDate(r.activated_at)}</td>
                  <td className="px-4 py-3">
                    <button onClick={() => togglePublic(r)} data-testid={`partner-toggle-${r.id}`}
                      className={"inline-flex items-center gap-1.5 border px-2.5 py-1 font-mono text-[9px] font-bold uppercase tracking-[0.15em] transition-colors " + (r.is_public ? "border-[#3ad07a]/60 text-[#3ad07a]" : "border-silver/30 text-silver/50 hover:border-gold/50")}>
                      {r.is_public ? <Eye size={11} /> : <EyeOff size={11} />} {r.is_public ? "Public" : "Hidden"}
                    </button>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end gap-2">
                      <button onClick={() => startEdit(r)} data-testid={`partner-edit-${r.id}`} className="p-1.5 text-silver/60 transition-colors hover:text-goldlight" aria-label="Edit partner"><Pencil size={14} /></button>
                      <button onClick={() => remove(r)} data-testid={`partner-delete-${r.id}`} className="p-1.5 text-silver/60 transition-colors hover:text-red-400" aria-label="Remove partner"><Trash2 size={14} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};
